import { ViewType } from "../Models/AppModel.js";
import { bindedModelBase } from "../Models/BindedModel.js";
import { AnatomicalSideEnum } from '../Models/CaseModel.js';
import { FullAnatomicalAxis } from "../Tools/AnatomicalAxis/FullAnatomicalAxis.js";
import { CalibrationTool } from "../Tools/Calibration/CalibrationTool.js";
import { CropPlaneTool } from '../Tools/CropPlaneTool.js';
import { CrossGoniometer } from "../Tools/CrossGoniometer.js";
import { MechanicalAxisAP } from "../Tools/DeformityAnalyzer/FullAnalyzerAP.js";
import { MechanicalAxisLT } from "../Tools/DeformityAnalyzer/FullAnalyzerLT.js";
import { MeasurementLine } from "../Tools/MeasurementLine.js";
import { Plane } from "../Tools/Plane.js";
import { RegisterHitPlane } from "../Tools/RegisterHitPlane.js";
import { RegisterPointsTool } from "../Tools/RegisterPointsTool.js";
import { RotationPlaneTool } from '../Tools/RotationPlaneTool.js';
import { getFirstUnsetBit, setBit, unsetBit, Utils } from "../Utils/Utils.js";
/**
 * Manages the initialization of the tools.
 */
export class ToolsInitBase {
    constructor(main, anatomicalAxisType = FullAnatomicalAxis, mechanicalAxisAPType = MechanicalAxisAP, mechanicalAxisLTType = MechanicalAxisLT) {
        this._main = main;
        this._anatomicalAxisType = anatomicalAxisType;
        this._mechanicalAxisAPType = mechanicalAxisAPType;
        this._mechanicalAxisLTType = mechanicalAxisLTType;
    }
    /**
     * Inits the rotation plane if necessary and returns it.
     */
    initRotationPlaneTool(viewType) {
        const tools = this._main.getTools(viewType);
        if (!tools.rotationPlaneTool) {
            tools.add(tools.rotationPlaneTool = new RotationPlaneTool(viewType, tools.plane));
        }
        return tools.rotationPlaneTool;
    }
    /**
     * Inits the crop plane tool if necessary and returns it.
     */
    initCropPlaneTool(viewType) {
        const tools = this._main.getTools(viewType);
        if (!tools.cropPlaneTool) {
            tools.add(tools.cropPlaneTool = new CropPlaneTool(viewType, tools.plane));
        }
        return tools.cropPlaneTool;
    }
    /**
     * Inits plane and the tool used to register points (used by calibration for example) for both scenes. Called once during app starting.
     */
    async initPlaneAndRegisterPlane() {
        return new Promise((resolve) => {
            let imgLoaded = 0;
            if (this._main.hasAP) {
                const toolsAP = this._main.toolsAP;
                toolsAP.add(toolsAP.registerHitPlane = new RegisterHitPlane(ViewType.AP), toolsAP.registerPointsTool = new RegisterPointsTool(ViewType.AP), toolsAP.plane = new Plane(this._main.caseModel.imgAP, ViewType.AP, () => {
                    if (!this._main.hasLT)
                        return resolve();
                    if (imgLoaded++ > 0)
                        return resolve();
                }));
            }
            if (this._main.hasLT) {
                const toolsLT = this._main.toolsLT;
                toolsLT.add(toolsLT.registerHitPlane = new RegisterHitPlane(ViewType.LT), toolsLT.registerPointsTool = new RegisterPointsTool(ViewType.LT), toolsLT.plane = new Plane(this._main.caseModel.imgLT, ViewType.LT, () => {
                    if (!this._main.hasAP)
                        return resolve();
                    if (imgLoaded++ > 0)
                        return resolve();
                }));
            }
        });
    }
    /**
     * Adds a line on the scene if the limit is not reached.
     */
    initLine(tools = this._main.activeTools, addToHistory = true) {
        const viewType = tools.viewType;
        const mask = viewType === ViewType.AP ? "linesMaskAP" : "linesMaskLT";
        const index = getFirstUnsetBit(bindedModelBase[mask], 7); // TODO: conf max
        if (index === -1)
            return null;
        const main = this._main;
        const camera = main.activeCamera;
        const points = MeasurementLine.getPoints(camera.position);
        let line = tools.lines[index];
        if (line) {
            if (addToHistory) {
                main.history.startTracking(viewType, line); // track new points too
            }
            line.setPoints(points.A, points.B);
        }
        else {
            if (addToHistory) {
                main.history.startTracking(viewType);
            }
            line = new MeasurementLine(viewType, index, points.A, points.B);
            line.onDeleted = () => {
                main.history.startTracking(viewType);
                bindedModelBase[mask] = unsetBit(bindedModelBase[mask], index);
                main.history.detectChanges(viewType);
                return false;
            };
            line.bindEvent('onDragStart', () => {
                main.history.startTracking(viewType, line);
            });
            line.bindEvent('onDragEnd', () => {
                main.history.detectChanges(viewType);
            });
            tools.lines.push(line);
            tools.add(line);
        }
        bindedModelBase[mask] = setBit(bindedModelBase[mask], index);
        if (addToHistory) {
            main.history.detectChanges(viewType);
        }
        return line;
    }
    /**
     * Adds a goniometer on the scene if the limit is not reached.
     */
    initCrossGoniometer(tools = this._main.activeTools, addToHistory = true) {
        const viewType = tools.viewType;
        const mask = viewType === ViewType.AP ? "goniometersMaskAP" : "goniometersMaskLT";
        const index = getFirstUnsetBit(bindedModelBase[mask], 7); // TODO: conf max
        if (index === -1)
            return null;
        const main = this._main;
        const camera = main.activeCamera;
        const points = CrossGoniometer.getPoints(camera.position);
        let goniometer = tools.goniometers[index];
        if (goniometer) {
            if (addToHistory) {
                main.history.startTracking(viewType, goniometer); // track new points too
            }
            goniometer.setPoints(points.A, points.B, points.C, points.D);
        }
        else {
            if (addToHistory) {
                main.history.startTracking(viewType);
            }
            goniometer = new CrossGoniometer(viewType, index, points.A, points.B, points.C, points.D);
            goniometer.onDeleted = () => {
                main.history.startTracking(viewType);
                bindedModelBase[mask] = unsetBit(bindedModelBase[mask], index);
                main.history.detectChanges(viewType);
                return false;
            };
            goniometer.bindEvent('onDragStart', () => {
                main.history.startTracking(viewType, goniometer);
            });
            goniometer.bindEvent('onDragEnd', () => {
                main.history.detectChanges(viewType);
            });
            tools.goniometers.push(goniometer);
            tools.add(goniometer);
        }
        bindedModelBase[mask] = setBit(bindedModelBase[mask], index);
        if (addToHistory) {
            main.history.detectChanges(viewType);
        }
        return goniometer;
    }
    /**
     * Inits the mechanical axis of a specific view if necessary and returns it.
     */
    initMechanicalAxis(viewType) {
        viewType === ViewType.AP && this._main.hasAP && this.initMechanicalAxisAP();
        viewType === ViewType.LT && this._main.hasLT && this.initMechanicalAxisLT();
    }
    /**
     * Inits the AP mechanical axis if necessary and returns it.
     */
    initMechanicalAxisAP() {
        const tools = this._main.toolsAP;
        if (!tools.mechanicalAxis) {
            tools.add(tools.mechanicalAxis = new this._mechanicalAxisAPType(this._main.caseModel.side === AnatomicalSideEnum.Left, this._main.caseModel.boneType, false, false));
            this._main.measuresController.bindMechanicalAxisAP();
            tools.mechanicalAxis.bindEvent('onDragStart', () => {
                this._main.history.startTracking(ViewType.AP, tools.mechanicalAxis);
            });
            tools.mechanicalAxis.bindEvent('onDragEnd', () => {
                this._main.history.detectChanges(ViewType.AP);
            });
        }
        return tools.mechanicalAxis;
    }
    /**
     * Inits the LT mechanical axis if necessary and returns it.
     */
    initMechanicalAxisLT() {
        const tools = this._main.toolsLT;
        if (!tools.mechanicalAxis) {
            tools.add(tools.mechanicalAxis = new this._mechanicalAxisLTType(this._main.caseModel.side === AnatomicalSideEnum.Left, this._main.caseModel.boneType, false)); //todo fix
            this._main.measuresController.bindMechanicalAxisLT();
            tools.mechanicalAxis.bindEvent('onDragStart', () => {
                this._main.history.startTracking(ViewType.LT, tools.mechanicalAxis);
            });
            tools.mechanicalAxis.bindEvent('onDragEnd', () => {
                this._main.history.detectChanges(ViewType.LT);
            });
        }
        return tools.mechanicalAxis;
    }
    /**
     * Inits the contralateral if necessary and returns it.
     */
    initControlaterMechanicalAxis() {
        const tools = this._main.toolsAP;
        if (!tools.contralateralMechanicalAxis) {
            tools.add(tools.contralateralMechanicalAxis = new this._mechanicalAxisAPType(this._main.caseModel.side !== AnatomicalSideEnum.Left, this._main.caseModel.boneType, true, false));
            this._main.measuresController.bindControlateralMechanicalAxis();
            tools.contralateralMechanicalAxis.bindEvent('onDragStart', () => {
                this._main.history.startTracking(ViewType.AP, tools.contralateralMechanicalAxis);
            });
            tools.contralateralMechanicalAxis.bindEvent('onDragEnd', () => {
                this._main.history.detectChanges(ViewType.AP);
            });
        }
        return tools.contralateralMechanicalAxis;
    }
    /**
     * Inits the calibration tool if necessary and returns it.
     */
    initCalibrationTool(viewType) {
        const tools = this._main.getTools(viewType);
        if (!tools.calibrationTool) {
            tools.add(tools.calibrationTool = new CalibrationTool(viewType, tools.plane));
            tools.calibrationTool.addEventListener('circlePointsError', () => {
                this._main.bridge.dispatchEvent({ type: 'onResult', args: 'calibrationCirclePointsError' });
            });
        }
        return tools.calibrationTool;
    }
    /**
     * Inits anatomical axis in both views.
     */
    initAnatomicalAxis() {
        this._main.hasAP && this.initAnatomicalAxisView(ViewType.AP);
        this._main.hasLT && this.initAnatomicalAxisView(ViewType.LT);
    }
    /**
     * Inits anatomical axis and returns it.
     */
    initAnatomicalAxisView(viewType) {
        const tools = this._main.getTools(viewType);
        if (!tools.anatomicalAxis) {
            const model = this._main.caseModel;
            const convexity = Utils.getConvexity(viewType, model.boneType, model.side, model.referenceType);
            tools.add(tools.anatomicalAxis = new this._anatomicalAxisType(viewType, convexity, model.referenceType, model.side, model.boneType, model.type, 0, 0, 0));
            tools.anatomicalAxis.bindEvent('onDragStart', () => {
                this._main.history.startTracking(viewType, tools.anatomicalAxis);
            });
            tools.anatomicalAxis.bindEvent('onDragEnd', () => {
                this._main.history.detectChanges(viewType);
            });
            tools.anatomicalAxis.addEventListener("startTracking", () => {
                this._main.history.startTracking(viewType, tools.anatomicalAxis);
            });
            tools.anatomicalAxis.addEventListener("detectChanges", () => {
                this._main.history.detectChanges(viewType);
            });
        }
        return tools.anatomicalAxis;
    }
}
//# sourceMappingURL=ToolsInit.js.map